import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FiLock, FiTrash2 } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import './GestaoUsuarios.css';

const GestaoUsuarios = () => {
  const { empreendimento_id } = useParams();
  const { hasPermission } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPermModal, setShowPermModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    nome: '',
    role_id: ''
  });
  const [permFormData, setPermFormData] = useState({
    usuario_id: '',
    permission_id: '',
    tipo: 'grant'
  });

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, rolesRes, permsRes] = await Promise.all([
        api.get(`/api/usuarios/empreendimento/${empreendimento_id}`),
        api.get('/api/usuarios/roles'),
        api.get('/api/usuarios/permissions')
      ]);
      setUsuarios(usersRes.data);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      alert('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [empreendimento_id]);

  useEffect(() => {
    if (empreendimento_id) {
      carregarDados();
    }
  }, [empreendimento_id, carregarDados]);

  const handleConvidar = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/usuarios/convidar', {
        ...formData,
        empreendimento_id
      });
      setShowModal(false);
      setFormData({ email: '', nome: '', role_id: '' });
      carregarDados();
      alert('Usuário convidado com sucesso!');
    } catch (error) {
      console.error('Erro ao convidar usuário:', error);
      alert(error.response?.data?.error || 'Erro ao convidar usuário');
    }
  };

  const handleAtualizarRole = async (usuario_id, role_id) => {
    try {
      await api.put(`/api/usuarios/${usuario_id}/role`, {
        empreendimento_id,
        role_id
      });
      carregarDados();
    } catch (error) {
      console.error('Erro ao atualizar role:', error);
      alert('Erro ao atualizar role');
    }
  };

  const handleRemoverAcesso = async (usuario_id) => {
    if (!window.confirm('Tem certeza que deseja remover o acesso deste usuário?')) {
      return;
    }
    try {
      await api.delete(`/api/usuarios/${usuario_id}/empreendimento/${empreendimento_id}`);
      carregarDados();
    } catch (error) {
      console.error('Erro ao remover acesso:', error);
      alert('Erro ao remover acesso');
    }
  };

  const handleAdicionarPermissao = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/usuarios/${permFormData.usuario_id}/permissions`, {
        permission_id: permFormData.permission_id,
        tipo: permFormData.tipo
      });
      setShowPermModal(false);
      setPermFormData({ usuario_id: '', permission_id: '', tipo: 'grant' });
      carregarDados();
    } catch (error) {
      console.error('Erro ao adicionar permissão:', error);
      alert('Erro ao adicionar permissão');
    }
  };

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  if (!hasPermission('users', 'view')) {
    return <div className="error">Você não tem permissão para visualizar esta página.</div>;
  }

  return (
    <div className="gestao-usuarios-page">
      <div className="page-header">
        <h1 className="page-title">Gestão de Usuários</h1>
        {hasPermission('users', 'invite') && (
          <button className="btn btn-primary" onClick={() => {
            setFormData({ email: '', nome: '', role_id: '' });
            setShowModal(true);
          }}>
            + Convidar Usuário
          </button>
        )}
      </div>

      {usuarios.length === 0 ? (
        <div className="empty-state">
          <p>Nenhum usuário cadastrado neste empreendimento.</p>
          {hasPermission('users', 'invite') && (
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              Convidar primeiro usuário
            </button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Role</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((usr) => (
                <tr key={usr.id}>
                  <td>{usr.nome}</td>
                  <td>{usr.email}</td>
                  <td>
                    {hasPermission('roles', 'assign') ? (
                      <select
                        value={usr.role_id}
                        onChange={(e) => handleAtualizarRole(usr.id, e.target.value)}
                        className="role-select"
                      >
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.descricao}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="badge badge-info">{usr.role_nome}</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      {hasPermission('permissions', 'override') && (
                        <button
                          className="btn btn-sm btn-secondary btn-icon"
                          onClick={() => {
                            setPermFormData({ ...permFormData, usuario_id: usr.id });
                            setShowPermModal(true);
                          }}
                          title="Permissões"
                        >
                          <FiLock />
                        </button>
                      )}
                      {hasPermission('users', 'deactivate') && (
                        <button
                          className="btn btn-sm btn-danger btn-icon"
                          onClick={() => handleRemoverAcesso(usr.id)}
                          title="Remover acesso"
                        >
                          <FiTrash2 />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Convidar Usuário</h2>
            <form onSubmit={handleConvidar}>
              <div className="form-group">
                <label>Nome *</label>
                <input
                  type="text"
                  required
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select
                  required
                  value={formData.role_id}
                  onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                >
                  <option value="">Selecione...</option>
                  {roles.filter(r => r.nome !== 'admin_sistema' && r.nome !== 'gestao_greencheck').map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.descricao}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Convidar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPermModal && (
        <div className="modal-overlay" onClick={() => setShowPermModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Adicionar Override de Permissão</h2>
            <form onSubmit={handleAdicionarPermissao}>
              <div className="form-group">
                <label>Usuário</label>
                <select
                  required
                  value={permFormData.usuario_id}
                  onChange={(e) => setPermFormData({ ...permFormData, usuario_id: e.target.value })}
                >
                  <option value="">Selecione...</option>
                  {usuarios.map((usr) => (
                    <option key={usr.id} value={usr.id}>
                      {usr.nome} ({usr.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Permissão</label>
                <select
                  required
                  value={permFormData.permission_id}
                  onChange={(e) => setPermFormData({ ...permFormData, permission_id: e.target.value })}
                >
                  <option value="">Selecione...</option>
                  {permissions.map((perm) => (
                    <option key={perm.id} value={perm.id}>
                      {perm.recurso}.{perm.acao} - {perm.descricao}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Tipo</label>
                <select
                  required
                  value={permFormData.tipo}
                  onChange={(e) => setPermFormData({ ...permFormData, tipo: e.target.value })}
                >
                  <option value="grant">Grant (Conceder)</option>
                  <option value="deny">Deny (Negar)</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPermModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Adicionar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestaoUsuarios;
